<template>
  <PainelLayout>
    <Breadcrumb class="print-no-show" :lista="[
      { 'name':'Painel', 'link' : '/' },
      { 'name':'Locações', 'link' : urlReturn },
      ]"
    />

    <div class="rental-details" :class="{ 'printing-history': printingHistory }" v-if="dados.id != null && loading == false">

      <!-- Cabeçalho -->
      <div class="page-head print-no-show">
        <div>
          <span class="eyebrow">Locação #{{ dados.id }}</span>
          <h1>Detalhes da locação</h1>
        </div>
        <div class="head-actions">
          <router-link v-show="!isOpeto" class="btn-ghost" :to="{ path: '/rental/new', query: { edit: true, id: dados.id } }">
            <i class="bi bi-pencil-fill"></i> Editar
          </router-link>
          <button v-show="isAdmin" @click="showModal_delete()" class="btn-ghost btn-ghost-danger">
            <i class="bi bi-trash3-fill"></i> Deletar
          </button>
          <button @click="imprimir()" class="btn-solid">
            <i class="bi bi-printer"></i> Imprimir
          </button>
        </div>
      </div>

      <div id="conteudoParaImprimir">

        <!-- Cabeçalho exclusivo para impressão: precisa estar dentro de
             #conteudoParaImprimir (e não como irmão antes dele) porque esse
             bloco é posicionado absoluto e cobre todo o resto da página —
             fora dele o cabeçalho ficava escondido embaixo, sem aparecer. -->
        <div class="header-print">
          <div class="print-brand">
            <strong>{{ systemName }}</strong>
            <span v-if="tenantName"> · {{ tenantName }}</span>
          </div>
          <h1>Locação #{{ dados.id }}</h1>
          <small>Impresso em {{ dateFormat.dateTime(new Date()) }}</small>
        </div>

        <!-- Hero: resumo + código da locação -->
        <div class="hero-row">
        <div class="hero-card">
          <div class="hero-top">
            <div>
              <span class="eyebrow">Cliente</span>
              <h2 class="client-name">{{ dados.cliente.name }}</h2>
            </div>
            <span class="status-badge" :class="'is-' + statusVariant(dados.status)">
              <i class="bi" :class="statusIcon(dados.status)"></i>
              {{ statusFormat.status(dados.status) }}
            </span>
          </div>

          <div class="hero-values">
            <div class="value-block value-block-main">
              <span class="value-label">Valor final</span>
              <span class="value-amount">R$ {{ dados.final_amount.toFixed(2) }}</span>
            </div>
            <div class="value-block">
              <span class="value-label">Valor total</span>
              <span class="value-amount-sm">R$ {{ dados.total_amount.toFixed(2) }}</span>
            </div>
            <div class="value-block">
              <span class="value-label">Desconto</span>
              <span class="value-amount-sm">R$ {{ dados.discount.toFixed(2) }}</span>
            </div>
          </div>

          <div class="hero-divider"></div>

          <div class="hero-meta">
            <div class="meta-item">
              <i class="bi bi-calendar-event"></i>
              <div>
                <span class="meta-label">Início</span>
                <span class="meta-value">{{ dateFormat.dateTime(dados.rental_date_start) }}</span>
              </div>
            </div>
            <div class="meta-item">
              <i class="bi bi-calendar-check"></i>
              <div>
                <span class="meta-label">Término</span>
                <span class="meta-value">{{ dateFormat.dateTime(dados.rental_date_end) }}</span>
              </div>
            </div>
            <div class="meta-item">
              <i class="bi bi-hourglass-split"></i>
              <div>
                <span class="meta-label">Duração</span>
                <span class="meta-value">{{ dados.days_amount }} {{ dados.days_amount == 1 ? 'dia' : 'dias' }}</span>
              </div>
            </div>
            <div class="meta-item" v-if="dados.status != 'PENDING_DELIVERY' && dados.status != 'PENDING_PICKUP'">
              <i class="bi bi-truck"></i>
              <div>
                <span class="meta-label">Entrega</span>
                <span class="meta-value">{{ dados.delivery_date }}</span>
              </div>
            </div>
            <div class="meta-item" v-if="dados.withdrawal">
              <i class="bi bi-box-arrow-in-down"></i>
              <div>
                <span class="meta-label">Coleta</span>
                <span class="meta-value">{{ dados.return_date }}</span>
              </div>
            </div>
            <div class="meta-item" v-if="!dados.withdrawal" v-show="dados.status != 'PENDING_DELIVERY' && dados.status != 'PENDING_PICKUP'">
              <i class="bi bi-arrow-return-left"></i>
              <div>
                <span class="meta-label">Devolução</span>
                <span class="meta-value">{{ dados.return_date }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="section-card rental-code-card" v-if="dados.code">
          <h3><i class="bi bi-qr-code"></i> Código da locação</h3>
          <div class="rental-code-content">
            <QrCode :value="dados.code" :size="120" />
            <span class="rental-code-text">{{ dados.code }}</span>
          </div>
        </div>
        </div>

        <!-- Entrega + Tabela de preços -->
        <div class="info-grid">
          <div class="section-card">
            <h3><i class="bi bi-geo-alt"></i> Entrega</h3>
            <div v-if="dados.withdrawal" class="pickup-note">
              <i class="bi bi-mailbox2-flag"></i>
              <span>Retirada na loja</span>
            </div>
            <div v-else class="address-block">
              <p>{{ dados.address }}, {{ dados.number }}</p>
              <p>{{ dados.neighbor }} — {{ dados.city }}/{{ dados.state }}</p>
              <p class="text-muted-sm">CEP {{ dados.zipcode }}</p>
              <AddressMap
                class="print-no-show"
                :address="dados.address"
                :number="dados.number"
                :neighbor="dados.neighbor"
                :city="dados.city"
                :state="dados.state"
                :zipcode="dados.zipcode"
                :latitude="dados.latitude"
                :longitude="dados.longitude"
              />
            </div>
            <div class="hero-note" v-show="dados.note">
              <i class="bi bi-sticky"></i>
              <span>{{ dados.note }}</span>
            </div>
          </div>

          <div class="section-card" v-show="dados.price_table != null">
            <h3><i class="bi bi-card-checklist"></i> Tabela de preços</h3>
            <p class="price-table-name">{{ dados?.price_table?.name }}</p>
          </div>
        </div>

        <!-- Itens -->
        <div class="section-card">
          <h3><i class="bi bi-box-seam"></i> Itens</h3>
          <table class="items-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th class="text-end">Quantidade</th>
                <th class="text-end">Valor p/uni</th>
                <th class="text-end">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in dados.items" :key="item.id">
                <td>{{ item.name }}</td>
                <td class="text-end">{{ item.quant }}</td>
                <td class="text-end">R$ {{ item.price.toFixed(2) }}</td>
                <td class="text-end fw-num">R$ {{ item.total.toFixed(2) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Ação contextual -->
        <div class="action-cta print-no-show" :class="'is-' + statusVariant(dados.status)"
             v-show="['PENDING_DELIVERY','PENDING_PICKUP','ACTIVE','PENDING_COMPANY_PICKUP','PENDING_RETURN'].includes(dados.status)">
          <router-link v-show="dados.status == 'PENDING_DELIVERY'" class="cta-btn" :to="{ path: '/rental/moviment', query: { to: dados.status, id: dados.id } }">
            <i class="bi bi-truck"></i> Marcar como entregue
          </router-link>
          <button v-if="isAdmin || isUser" v-show="dados.status == 'ACTIVE'" class="cta-btn"
                  @click="sendMovimentacao(dados.withdrawal ? 'PENDING_RETURN' : 'PENDING_COMPANY_PICKUP','FORCE_COMPLETED')">
            <i class="bi bi-check-circle-fill"></i> Finalizar locação
          </button>
          <router-link v-show="dados.status == 'PENDING_PICKUP'" class="cta-btn" :to="{ path: '/rental/moviment', query: { to: dados.status, id: dados.id } }">
            <i class="bi bi-box-arrow-left"></i> Marcar como retirada
          </router-link>
          <router-link v-show="dados.status == 'PENDING_COMPANY_PICKUP'" class="cta-btn" :to="{ path: '/rental/moviment', query: { to: dados.status, id: dados.id } }">
            <i class="bi bi-box-seam"></i> Marcar como recolhida
          </router-link>
          <router-link v-show="dados.status == 'PENDING_RETURN'" class="cta-btn" :to="{ path: '/rental/moviment', query: { to: dados.status, id: dados.id } }">
            <i class="bi bi-check-square"></i> Marcar como devolvida
          </router-link>
        </div>

        <!-- Histórico: não entra na impressão geral da locação — só na impressão
             dedicada do próprio histórico (botão "Imprimir histórico" acima). -->
        <div class="section-card print-no-show" v-if="dados.actions.length > 0">
          <div class="section-card-head">
            <h3><i class="bi bi-clock-history"></i> Histórico de movimentações</h3>
            <button type="button" class="btn-ghost" @click="imprimirHistorico()">
              <i class="bi bi-printer"></i> Imprimir histórico
            </button>
          </div>
          <ul class="timeline">
            <li v-for="action in dados.actions" :key="action.id">
              <span class="timeline-dot"></span>
              <div class="timeline-content">
                <div class="timeline-top">
                  <strong>{{ statusFormat.type(action.type) }}</strong>
                  <span class="timeline-date">{{ dateFormat.dateTime(action.created_at) }}</span>
                </div>
                <p v-if="action.description">{{ action.description }}</p>
                <span class="timeline-user"><i class="bi bi-person"></i> {{ action.user.name }}</span>

                <!-- Fotos da movimentação: nunca na impressão, e nunca para o nível operador
                     (o backend já nem envia a URL das fotos pra esse nível) -->
                <div class="timeline-photos print-no-show" v-if="!isOpeto && action.photos && action.photos.length">
                  <a v-for="(photo, idx) in action.photos" :key="idx" :href="photo" target="_blank" rel="noopener">
                    <img :src="photo" alt="Foto da movimentação" />
                  </a>
                </div>
              </div>
            </li>
          </ul>
        </div>

      </div>

      <!-- Impressão exclusiva do histórico de movimentações, com fotos em tamanho maior.
           Só fica visível quando .printing-history está ativa (ver imprimirHistorico()). -->
      <div id="historicoParaImprimir">
        <div class="print-history-header">
          <div class="print-brand">
            <strong>{{ systemName }}</strong>
            <span v-if="tenantName"> · {{ tenantName }}</span>
          </div>
          <h1>Histórico de movimentações</h1>
          <div class="print-history-meta">
            <span><strong>Locação</strong> #{{ dados.id }}</span>
            <span><strong>Código</strong> {{ dados.code }}</span>
            <span><strong>Cliente</strong> {{ dados.cliente.name }}</span>
          </div>
          <div class="print-history-timestamp">Impresso em {{ dateFormat.dateTime(new Date()) }}</div>
        </div>
        <ul class="print-history-list">
          <li v-for="action in dados.actions" :key="'print-' + action.id">
            <div class="print-history-top">
              <strong>{{ statusFormat.type(action.type) }}</strong>
              <span>{{ dateFormat.dateTime(action.created_at) }}</span>
            </div>
            <p v-if="action.description">{{ action.description }}</p>
            <span class="print-history-user">{{ action.user.name }}</span>
            <div class="print-history-photos" v-if="action.photos && action.photos.length">
              <img v-for="(photo, idx) in action.photos" :key="idx" :src="photo" alt="Foto da movimentação" />
            </div>
          </li>
        </ul>
      </div>
    </div>

    <!-- Div Loading -->
    <div v-else class="d-flex justify-content-center p-3">
      <div class="spinner-border" style="width: 7rem; height: 7rem;" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Modal de Delete -->
    <div class="modal fade print-no-show" id="modal_delete" tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true" data-bs-backdrop="static">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
              <h5 class="modal-title">Confirmar Exclusão</h5>
              <button @click="closeModal" type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body text-center">
            <i class="bi bi-exclamation-triangle-fill text-warning" style="font-size: 3rem;"></i>
            <p class="mt-3">Tem certeza que deseja excluir a locação de <strong>{{ dados.cliente.name }}</strong> ({{ dados.city }})?</p>
            <p class="text-muted small">Esta ação não pode ser desfeita e pode afetar o estoque.</p>
          </div>
          <div class="modal-footer">
              <button type="button" @click="closeModal" class="btn btn-secondary">Cancelar</button>
              <button type="button" @click="confirmExcluir" :disabled="deleteLoading" class="btn btn-danger">
                <span v-if="deleteLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span v-else>Excluir</span>
              </button>
          </div>
        </div>  
      </div>
    </div>    
  </PainelLayout>
</template>

<script>
import PainelLayout from '@/layouts/PainelLayout.vue';
import { rental } from '@/models/rental';
import { onMounted, onUnmounted, nextTick, ref, reactive, computed } from 'vue';
import Breadcrumb from '@/components/itens/breadcrumb.vue';
import AddressMap from '@/components/AddressMap.vue';
import QrCode from '@/components/QrCode.vue';
import dateFormat from '@/services/dateFormat.js';
import { Modal } from 'bootstrap';
import statusFormat from '@/services/statusFormat';
import { usePermissions } from '@/composables/usePermissions';
import { useAuth } from '@/services/auth.js';
import { useToastStore } from '@/stores/toastStore';
import { parseError } from '@/utils/errorHelper';
import router from '@/router';

export default {
  name: "DetailsView",
  components: { PainelLayout, Breadcrumb, AddressMap, QrCode },
  setup(props) {

    const urlReturn = '/rental';

    function goReturn() {
      router.push(urlReturn);
    }

    var deleteLoading = ref(false);

    const query = window.location.search;
    const urlParams = new URLSearchParams(query);
    const id = urlParams.get('id');
    const toastStore = useToastStore();
    var loading = ref(false);
    const model = rental();

    var modal = '';

    const { isAdmin, isUser, isOpeto } = usePermissions();

    // Cabeçalho padrão de impressão: nome do sistema + nome da empresa de locação (tenant)
    const auth = useAuth();
    const systemName = process.env.VUE_APP_NAME;
    const tenantName = computed(() => auth.tenant?.name || '');

    function openModal(elemento) {
      const modalObj = document.getElementById(elemento);
      modal = new Modal(modalObj);
      modal.show();
    };

    function closeModal() {
      modal.hide();
    };

    function showModal_delete() {
      openModal('modal_delete');
    }

    function confirmExcluir() {
      deleteLoading.value = true;
      model.delet(dados.id).then(() => {
        toastStore.success('Locação excluída com sucesso.');
        closeModal();
        goReturn();
      }).catch((error) => {
        deleteLoading.value = false;
        toastStore.error('Erro ao excluir locação: ' + parseError(error));
      });
    }

    // Mapeia o status para uma variante visual (cor do badge, do CTA, etc.)
    function statusVariant(status) {
      switch (status) {
        case 'PENDING_DELIVERY':
        case 'PENDING_PICKUP':
          return 'pending';
        case 'ACTIVE':
          return 'active';
        case 'PENDING_RETURN':
        case 'PENDING_COMPANY_PICKUP':
          return 'returning';
        case 'COMPLETED':
          return 'completed';
        case 'CANCELLED':
          return 'cancelled';
        default:
          return 'neutral';
      }
    }

    function statusIcon(status) {
      switch (status) {
        case 'PENDING_DELIVERY':
        case 'PENDING_PICKUP':
          return 'bi-hourglass-split';
        case 'ACTIVE':
          return 'bi-play-circle-fill';
        case 'PENDING_RETURN':
        case 'PENDING_COMPANY_PICKUP':
          return 'bi-arrow-return-left';
        case 'COMPLETED':
          return 'bi-check-circle-fill';
        case 'CANCELLED':
          return 'bi-x-circle-fill';
        default:
          return 'bi-dot';
      }
    }

    var dados = reactive({
      cliente: {name:'AVULSO'},
      price_table: null,
      items: [],
      id : null,
      code: null,
      discount: 0,
      actions: [],
      percent: 0,
      total_amount: 0,
      final_amount: 0,
      rental_date_start: '',
      rental_time_start: '00:00',
      rental_date_end: '',
      rental_time_end: '23:59',
      days_amount: 1,
      status: null,
      withdrawal: false,
      address: '',
      number: '',
      neighbor: '',
      city: '',
      state: '',
      zipcode: '',
      latitude: null,
      longitude: null,
      note: ''
    })

    function imprimir() {
      window.print();
    }

    // Impressão só do histórico de movimentações, com as fotos em tamanho maior.
    // Alterna uma classe que troca qual bloco fica visível em @media print
    // (ver CSS .printing-history) e volta ao normal assim que a impressão fecha.
    const printingHistory = ref(false);

    function imprimirHistorico() {
      printingHistory.value = true;
      nextTick(() => window.print());
    }

    function onAfterPrint() {
      printingHistory.value = false;
    }

    window.addEventListener('afterprint', onAfterPrint);
    onUnmounted(() => window.removeEventListener('afterprint', onAfterPrint));

    function carregarDados() {
      loading.value = true;
      model.getOne(id).then((response) => {
        dados.id = response.id;
        dados.code = response.code;
        // Locações "avulsas" (sem cliente vinculado) vêm com client: null do backend
        dados.cliente = response.client || { name: 'Cliente não informado' };
        dados.items = response.items;
        dados.listaitens = response.listaitens;
        dados.price_table = response.price_table;
        dados.actions = response.actions;
        dados.discount = response.discount;
        dados.percent = response.percent;
        dados.total_amount = response.total_amount;
        dados.final_amount = response.final_amount;
        dados.rental_date_start = response.rental_date_start;
        dados.rental_time_start = response.rental_time_start;
        dados.rental_date_end = response.rental_date_end;
        dados.rental_time_end = response.rental_time_end;
        dados.days_amount = response.days_amount;
        dados.withdrawal = response.withdrawal;
        dados.address = response.address;
        dados.number = response.number;
        dados.neighbor = response.neighbor;
        dados.city = response.city;
        dados.state = response.state;
        dados.status = response.status;
        dados.zipcode = response.zipcode;
        dados.latitude = response.latitude;
        dados.longitude = response.longitude;
        dados.note = response.note;

        loading.value = false;
      }).catch((error) => {
        loading.value = false;
        toastStore.error('Erro ao carregar detalhes da locação: ' + parseError(error));
      });
    }

    onMounted(() => {
      carregarDados();
    });

    function sendMovimentacao(status, type) {
      loading.value = true;
      var description =  'Encerramento manual da locação';
      var note =  '';
      model.sendMovimentacao(dados.id, status, type, note, description).then((response) => {
        if(response) {
          toastStore.success('Locação finalizada com sucesso!');
          carregarDados();
        } else {
          toastStore.error('Erro ao finalizar locação.');
          loading.value = false;
        } 
      }).catch((error) => {
        loading.value = false;
        toastStore.error('Erro ao finalizar locação: ' + parseError(error));
      });
    }

    return {
      dados,dateFormat,statusFormat,loading,imprimir,sendMovimentacao,isAdmin,isUser,isOpeto,
      showModal_delete,confirmExcluir,closeModal,goReturn,urlReturn,deleteLoading,
      statusVariant,statusIcon,printingHistory,imprimirHistorico,systemName,tenantName
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@600;700;800&family=Inter:wght@400;500;600&display=swap');

.rental-details {
  --ink: #16223A;
  --ink-soft: #4B5A72;
  --line: #E4E8EF;
  --surface: #FFFFFF;
  --canvas: #F3F5F8;
  --brand: #1E4B6B;
  --brand-soft: #EAF1F6;
  --amber: #B4790E;
  --amber-soft: #FBF1DE;
  --teal: #167A5B;
  --teal-soft: #E4F5EE;
  --blue: #2E5FA3;
  --blue-soft: #E9F0FA;
  --gray: #64748B;
  --gray-soft: #EEF1F5;
  --red: #B3362C;
  --red-soft: #FBEAE8;
  font-family: 'Inter', sans-serif;
  color: var(--ink);
}

.rental-details h1, .rental-details h2, .rental-details h3 {
  font-family: 'Manrope', sans-serif;
  margin: 0;
}

.eyebrow {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-soft);
  margin-bottom: 2px;
}

/* Cabeçalho */
.page-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin: 8px 0 20px;
  flex-wrap: wrap;
  gap: 12px;
}
.page-head h1 {
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--ink);
}
.head-actions {
  display: flex;
  gap: 8px;
}

.btn-ghost, .btn-solid {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink);
  text-decoration: none;
  cursor: pointer;
  transition: border-color .15s ease, background .15s ease;
}
.btn-ghost:hover { border-color: var(--brand); color: var(--brand); }
.btn-ghost-danger:hover { border-color: var(--red); color: var(--red); }
.btn-solid {
  background: var(--ink);
  color: #fff;
  border-color: var(--ink);
}
.btn-solid:hover { background: #0E1930; }

/* Hero */
.hero-row {
  display: flex;
  align-items: stretch;
  gap: 16px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}
.hero-card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 28px 28px 22px;
  flex: 1 1 480px;
}
.hero-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}
.client-name {
  font-size: 1.55rem;
  font-weight: 700;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 999px;
  white-space: nowrap;
}
.status-badge.is-pending { background: var(--amber-soft); color: var(--amber); }
.status-badge.is-active { background: var(--teal-soft); color: var(--teal); }
.status-badge.is-returning { background: var(--blue-soft); color: var(--blue); }
.status-badge.is-completed { background: var(--gray-soft); color: var(--gray); }
.status-badge.is-cancelled { background: var(--red-soft); color: var(--red); }
.status-badge.is-neutral { background: var(--gray-soft); color: var(--gray); }

.hero-values {
  display: flex;
  align-items: baseline;
  gap: 36px;
  margin-top: 20px;
  flex-wrap: wrap;
}
.value-label {
  display: block;
  font-size: 0.72rem;
  color: var(--ink-soft);
  font-weight: 600;
  margin-bottom: 2px;
}
.value-amount {
  font-family: 'Manrope', sans-serif;
  font-size: 2.1rem;
  font-weight: 800;
  color: var(--brand);
}
.value-amount-sm {
  font-family: 'Manrope', sans-serif;
  font-size: 1.15rem;
  font-weight: 700;
}

.hero-divider {
  height: 1px;
  background: var(--line);
  margin: 22px 0 18px;
}

.hero-meta {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
}
.meta-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.meta-item i {
  color: var(--brand);
  font-size: 1.05rem;
  margin-top: 2px;
}
.meta-label {
  display: block;
  font-size: 0.72rem;
  color: var(--ink-soft);
}
.meta-value {
  display: block;
  font-weight: 600;
  font-size: 0.92rem;
}

.hero-note {
  margin-top: 18px;
  padding: 12px 14px;
  background: var(--canvas);
  border-radius: 10px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 0.88rem;
  color: var(--ink-soft);
}
.hero-note i { margin-top: 2px; }

/* Grid de informações */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

.section-card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 20px 22px;
  margin-bottom: 16px;
}
.section-card h3 {
  font-size: 0.95rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  color: var(--ink);
}
.section-card h3 i { color: var(--brand); }
.section-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.section-card-head h3 { margin-bottom: 0; }

.address-block p { margin: 0 0 4px; font-size: 0.9rem; }
.text-muted-sm { color: var(--ink-soft); font-size: 0.82rem; }
.pickup-note {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: var(--ink-soft);
}
.price-table-name { font-weight: 600; font-size: 0.92rem; }

.rental-code-card {
  flex: 0 1 220px;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.rental-code-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.rental-code-text {
  font-family: 'Manrope', sans-serif;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--ink);
}

/* Tabela de itens */
.items-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
}
.items-table th {
  text-align: left;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ink-soft);
  font-weight: 700;
  padding: 8px 10px;
  border-bottom: 1px solid var(--line);
}
.items-table td {
  padding: 10px 10px;
  border-bottom: 1px solid var(--line);
}
.items-table tr:last-child td { border-bottom: none; }
.text-end { text-align: right; }
.fw-num { font-weight: 700; color: var(--brand); }

/* CTA de ação */
.action-cta {
  display: flex;
  justify-content: center;
  padding: 16px;
  border-radius: 14px;
  margin-bottom: 16px;
}
.action-cta.is-pending { background: var(--amber-soft); }
.action-cta.is-active { background: var(--teal-soft); }
.action-cta.is-returning { background: var(--blue-soft); }
.cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 0.92rem;
  padding: 10px 20px;
  border-radius: 10px;
  border: none;
  background: var(--ink);
  color: #fff;
  cursor: pointer;
  text-decoration: none;
}
.cta-btn:hover { background: #0E1930; color: #fff; }

/* Timeline */
.timeline {
  list-style: none;
  margin: 0;
  padding: 0;
  position: relative;
}
.timeline li {
  position: relative;
  padding-left: 26px;
  padding-bottom: 20px;
}
.timeline li:last-child { padding-bottom: 0; }
.timeline li::before {
  content: '';
  position: absolute;
  left: 5px;
  top: 14px;
  bottom: -6px;
  width: 1px;
  background: var(--line);
}
.timeline li:last-child::before { display: none; }
.timeline-dot {
  position: absolute;
  left: 0;
  top: 4px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: var(--brand);
  border: 2px solid var(--brand-soft);
}
.timeline-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  font-size: 0.88rem;
}
.timeline-date { color: var(--ink-soft); font-size: 0.8rem; }
.timeline-content p {
  margin: 4px 0;
  font-size: 0.85rem;
  color: var(--ink-soft);
}
.timeline-user {
  font-size: 0.78rem;
  color: var(--ink-soft);
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.timeline-photos {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}
.timeline-photos img {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid var(--line);
}

/* Impressão */

/* Cabeçalho padrão: nome do sistema + nome da empresa de locação (tenant).
   Aparece em toda impressão desta página (geral e do histórico). */
.print-brand {
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #777;
  margin-bottom: 6px;
}
.print-brand strong {
  color: #333;
  font-weight: 800;
}

.header-print {
  display: none;
}

/* Impressão exclusiva do histórico (ver imprimirHistorico()): fora da impressão
   fica sempre fora do fluxo; dentro dela, só aparece quando .printing-history
   está ativa na .rental-details (troca de lugar com #conteudoParaImprimir). */
#historicoParaImprimir {
  display: none;
}
.print-history-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #333;
  font-family: 'Manrope', sans-serif;
}
.print-history-header h1 {
  font-size: 1.5rem;
  font-weight: 800;
  margin: 0 0 10px;
}
.print-history-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 24px;
  font-size: 0.9rem;
  color: #333;
}
.print-history-meta strong {
  font-weight: 700;
  margin-right: 4px;
}
.print-history-timestamp {
  margin-top: 8px;
  font-size: 0.78rem;
  color: #777;
}
.print-history-list {
  list-style: none;
  margin: 20px 0 0;
  padding: 0;
}
.print-history-list li {
  padding: 16px 0;
  border-bottom: 1px solid #ccc;
  break-inside: avoid;
}
.print-history-list li:last-child { border-bottom: none; }
.print-history-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 1rem;
  font-weight: 700;
}
.print-history-list p { margin: 6px 0; color: #333; }
.print-history-user { font-size: 0.85rem; color: #555; }
.print-history-photos {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 12px;
}
.print-history-photos img {
  width: 280px;
  height: 280px;
  object-fit: cover;
  border-radius: 0;
  border: 1px solid #000;
  break-inside: avoid;
}

@media print {
  /* O reset "esconde tudo" mora no <style> global no fim do arquivo — dentro
     de <style scoped> o Vue exige que o próprio <body> carregue o atributo
     de escopo do componente pra "body *" bater, o que nunca acontece (o body
     é externo ao componente), então aqui essa regra nunca surtia efeito. */

  .print-no-show {
    display: none !important;
  }

  .header-print {
    display: block;
    margin-bottom: 20px;
    font-family: 'Manrope', sans-serif;
  }
  /* Sem visibility:visible aqui de propósito: .header-print agora mora dentro
     de #conteudoParaImprimir, então herda a visibilidade de lá — inclusive
     ficando escondido junto quando .printing-history troca pro histórico. */

  #conteudoParaImprimir,
  #conteudoParaImprimir * {
    visibility: visible;
  }

  #conteudoParaImprimir {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    margin: 0;
  }

  table, tr, td, th {
    page-break-inside: avoid;
  }

  /* Troca: enquanto .printing-history estiver ativa, esconde o conteúdo
     normal e mostra só o histórico (com fotos maiores). */
  .printing-history #conteudoParaImprimir,
  .printing-history #conteudoParaImprimir * {
    visibility: hidden;
  }

  .printing-history #historicoParaImprimir,
  .printing-history #historicoParaImprimir * {
    visibility: visible;
  }

  .printing-history #historicoParaImprimir {
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    margin: 0;
  }

  /* Documento oficial: preto e branco, bordas sólidas nos cartões internos,
     texto compacto. (o tamanho geral da fonte já encolhe via "html { font-size }"
     no <style> global no fim do arquivo — aqui só o preto/borda/proporção) */
  #conteudoParaImprimir *,
  #historicoParaImprimir * {
    color: #000 !important;
  }

  .hero-card, .section-card, .print-history-list li {
    border: 1px solid #000 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    break-inside: avoid;
  }

  .header-print, .print-history-header {
    border-bottom: 2px solid #000;
  }

  .status-badge {
    background: none !important;
    border: 1px solid #000;
    border-radius: 3px;
  }

  .hero-note {
    background: none !important;
    border: 1px dashed #000;
  }

  .items-table th,
  .items-table td {
    border: 1px solid #000 !important;
  }

  .timeline-dot {
    background: #000 !important;
    border-color: #000 !important;
  }

  .client-name { font-size: 1.15rem; }
  .value-amount { font-size: 1.4rem; }

  /* Espaçamento compacto: aperta as margens/preenchimentos entre os blocos
     pra caber mais conteúdo por página, sem depender só do zoom de fonte. */
  #conteudoParaImprimir, #historicoParaImprimir { padding: 0; }
  .header-print, .print-history-header { margin-bottom: 10px; padding-bottom: 8px; }
  .print-history-timestamp { margin-top: 2px; }

  .hero-row { gap: 8px; margin-bottom: 8px; }
  .hero-card { padding: 10px 12px; }
  .hero-values { gap: 18px; margin-top: 8px; }
  .hero-divider { margin: 8px 0; }
  .hero-meta { gap: 6px 16px; }
  .hero-note { margin-top: 8px; padding: 6px 8px; }

  .info-grid { gap: 8px; margin-bottom: 8px; }
  .section-card { padding: 10px 12px; margin-bottom: 8px; }
  .section-card h3, .section-card-head { margin-bottom: 8px; }
  .rental-code-content { gap: 4px; }

  .items-table th, .items-table td { padding: 4px 6px; }

  .timeline li { padding-bottom: 10px; }
  .print-history-list { margin-top: 8px; }
  .print-history-list li { padding: 8px 10px; }
  .print-history-photos { gap: 8px; margin-top: 6px; }
}

@media (max-width: 576px) {
  .page-head { flex-direction: column; align-items: flex-start; }
  .hero-values { gap: 20px; }
  .value-amount { font-size: 1.6rem; }
}
</style>

<!-- Sem "scoped" de propósito: o truque de impressão ("esconde tudo, revela só
     #conteudoParaImprimir/#historicoParaImprimir") precisa que a regra bata no
     <body> de verdade, e o <body> nunca recebe o atributo de escopo do Vue —
     então essa regra específica tem que ficar fora do <style scoped> acima.
     Sem ela, nada global (ex.: o menu lateral do painel) era escondido na
     impressão. O mesmo vale pro tamanho de fonte "documento oficial": como o
     app usa rem em toda parte, reduzir o font-size do <html> (que também
     precisa estar fora do escopo) encolhe tudo de uma vez, com proporção. -->
<style>
@media print {
  /* Encolhe a margem que o navegador reserva por padrão pra impressão
     (tipicamente ~1.5–2cm de cada lado) — é esse espaço em branco entre a
     borda da página e a moldura preta do documento que sobrava demais nas
     laterais. Mantém um pouco mais em cima/embaixo. */
  @page {
    margin: 15mm 8mm;
  }

  html {
    font-size: 75%;
  }

  body * {
    visibility: hidden;
  }
}
</style>